/**
 * Translation Management Component (Admin Only)
 * Allows admin users to manage translation keys and translations
 */

import { useState, useEffect } from 'react';
import { apiClient } from '@/services/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import {
  Loader2,
  Plus,
  Edit,
  Check,
  Languages,
  RefreshCw,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAppTranslation } from '@/contexts/TranslationContext';
interface TranslationKey {
  id: number;
  key_name: string;
  category: string | null;
  description: string | null;
  source_text: string;
  is_active: boolean;
  needs_review: boolean;
  // TRUS-446. Tenant-authored keys (custom fields, form sections, picklist
  // options) are registered with is_dynamic = true and carry the source row
  // they came from. The backend has returned these since TRUS-378; this screen
  // simply never asked for them, which is why an org's own field labels were
  // invisible here.
  is_dynamic?: boolean;
  cbo_id?: number | null;
  source_kind?: string | null;
  source_ref?: string | null;
}

/** One row of "what this org still owes a translator, and why". */
interface OutstandingItem {
  key_name: string;
  category: string | null;
  source_kind: string | null;
  source_text: string;
  context: string | null;
  reason: string;
  current_translation: string | null;
  translated_from: string | null;
}

interface OutstandingReport {
  cbo_id: number;
  language_code: string;
  total_keys: number;
  missing: number;
  stale: number;
  unverified: number;
  by_category: Record<string, Record<string, number>>;
  items: OutstandingItem[];
}

/** Which slice of keys the table is showing. */
type KeyScope = 'all' | 'tenant' | 'chrome';

// The backend caps a page at 10,000 but defaults to 2,000. Registration takes
// an org from ~73 keys to several hundred (VYC: 1,853), so the table has to
// page rather than silently truncate at the default.
const PAGE_SIZE = 200;

interface Translation {
  id: number;
  translation_key_id: number;
  language_code: string;
  translated_text: string;
  translation_method: string;
  is_verified: boolean;
}

interface Language {
  id: number;
  language_code: string;
  language_name: string;
  native_name: string;
  translation_progress: number;
}

export function TranslationManagement() {
  const { t } = useAppTranslation();
  const { user, loading: authLoading } = useAuth();

  const [translationKeys, setTranslationKeys] = useState<TranslationKey[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<TranslationKey | null>(null);
  const [translations, setTranslations] = useState<Translation[]>([]);
  const [showAddKeyDialog, setShowAddKeyDialog] = useState(false);
  const [showEditTranslationDialog, setShowEditTranslationDialog] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState<Translation | null>(null);

  // TRUS-446 — tenant-content controls.
  const [scope, setScope] = useState<KeyScope>('all');
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [outstanding, setOutstanding] = useState<OutstandingReport | null>(null);
  const [loadingOutstanding, setLoadingOutstanding] = useState(false);
  const [registering, setRegistering] = useState(false);

  // Check if user has admin access
  const isAdmin = user?.role === 'super_admin' || user?.role === 'org_admin' || user?.role === 'ops_admin' || user?.role === 'administrator';

  // Form state for new translation key
  const [newKey, setNewKey] = useState({
    key_name: '',
    category: '',
    description: '',
    source_text: '',
  });

  // Form state for editing translation
  const [editTranslation, setEditTranslation] = useState({
    language_code: '',
    translated_text: '',
  });

  useEffect(() => {
    // Wait for auth to finish loading before checking admin status
    if (authLoading) {
      return;
    }

    if (isAdmin) {
      fetchData();
    } else {
      setLoading(false);
    }
    // Scope, search and page are server-side filters, so a change to any of
    // them is a refetch rather than a client-side filter over a truncated page.
  }, [isAdmin, authLoading, scope, appliedSearch, page]);

  // Show loading while auth is still loading
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show access denied if user is not admin
  if (!isAdmin) {
    return (
      <Card className="max-w-2xl mx-auto mt-8">
        <CardHeader>
          <CardTitle>{t('admin.access_denied', 'Access Denied')}</CardTitle>
          <CardDescription>
            {t('admin.you_need_administrator_privileges_to', 'You need administrator privileges to access translation management.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t('admin.please_contact_your_system_administrator', 'Please contact your system administrator if you believe you should have access to this feature.')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const fetchData = async () => {
    try {
      setLoading(true);
      // TRUS-446: the call used to send no parameters at all, so it returned
      // the first 2,000 keys with no way to filter or page. An org that has
      // registered its tenant content has far more than that, and the rows a
      // translator actually needs were buried among the chrome.
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (scope === 'tenant') params.is_dynamic = 'true';
      if (scope === 'chrome') params.is_dynamic = 'false';
      if (appliedSearch.trim()) params.search = appliedSearch.trim();

      const [keysResponse, languagesResponse] = await Promise.all([
        apiClient.get('/api/admin/translation-keys', { params }),
        apiClient.get('/api/languages'),
      ]);

      setTranslationKeys(keysResponse.data);
      setLanguages(languagesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error', {
        description: 'Failed to load translation data',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Re-scan this org's fields, forms and section headings and register any
   * string that is not yet a translation key.
   *
   * Idempotent — a run over unchanged content writes nothing. Newly registered
   * keys carry the ENGLISH source only; the backend seeds Spanish for
   * platform-shipped labels (standard profile fields, section headings) from
   * WeaveIn's own catalog, and never for tenant-authored content.
   */
  const handleRegisterTenantContent = async () => {
    try {
      setRegistering(true);
      const { data } = await apiClient.post('/api/admin/translations/dynamic/register', {});
      const seeded = Object.values(data?.platform_seeded ?? {}).reduce(
        (sum: number, r: unknown) => sum + Number((r as { seeded?: number })?.seeded ?? 0),
        0
      );
      toast.success(t('admin.registerComplete', 'Tenant content registered'), {
        description: t(
          'admin.registerResult',
          '{{created}} new, {{updated}} updated, {{unchanged}} unchanged. {{seeded}} platform labels pre-filled with Spanish.',
          {
            created: data?.created ?? 0,
            updated: data?.updated ?? 0,
            unchanged: data?.unchanged ?? 0,
            seeded,
          }
        ),
      });
      setPage(0);
      await fetchData();
      if (outstanding) await fetchOutstanding();
    } catch (error) {
      console.error('Error registering tenant content:', error);
      toast.error('Error', {
        description: t('admin.registerFailed', 'Could not register tenant content'),
      });
    } finally {
      setRegistering(false);
    }
  };

  /** What still needs a human: never translated, gone stale, or unverified. */
  const fetchOutstanding = async () => {
    try {
      setLoadingOutstanding(true);
      const { data } = await apiClient.get(
        '/api/admin/translations/dynamic/outstanding',
        { params: { language_code: 'es', limit: 500 } }
      );
      setOutstanding(data);
    } catch (error) {
      console.error('Error loading outstanding report:', error);
      toast.error('Error', {
        description: t('admin.outstandingFailed', 'Could not load outstanding translations'),
      });
    } finally {
      setLoadingOutstanding(false);
    }
  };

  const fetchTranslationsForKey = async (keyId: number) => {
    try {
      const response = await apiClient.get(`/api/admin/translations/${keyId}`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const handleSelectKey = async (key: TranslationKey) => {
    setSelectedKey(key);
    await fetchTranslationsForKey(key.id);
  };

  const handleAddKey = async () => {
    try {
      await apiClient.post('/api/admin/translation-keys', newKey);

      toast.success('Success', {
        description: 'Translation key added successfully',
      });

      setShowAddKeyDialog(false);
      setNewKey({
        key_name: '',
        category: '',
        description: '',
        source_text: '',
      });

      await fetchData();
    } catch (error) {
      console.error('Error adding translation key:', error);
      toast.error('Error', {
        description: 'Failed to add translation key',
      });
    }
  };

  const handleAddOrUpdateTranslation = async () => {
    if (!selectedKey) return;

    try {
      await apiClient.post('/api/admin/translations', {
        translation_key_id: selectedKey.id,
        language_code: editTranslation.language_code,
        translated_text: editTranslation.translated_text,
        translation_method: 'manual',
      });

      toast.success('Success', {
        description: 'Translation saved successfully',
      });

      setShowEditTranslationDialog(false);
      setEditTranslation({ language_code: '', translated_text: '' });
      await fetchTranslationsForKey(selectedKey.id);
    } catch (error) {
      console.error('Error saving translation:', error);
      toast.error('Error', {
        description: 'Failed to save translation',
      });
    }
  };

  const handleEditTranslation = (translation: Translation) => {
    setSelectedTranslation(translation);
    setEditTranslation({
      language_code: translation.language_code,
      translated_text: translation.translated_text,
    });
    setShowEditTranslationDialog(true);
  };

  const handleVerifyTranslation = async (translationId: number) => {
    try {
      await apiClient.put(`/api/admin/translations/${translationId}`, {
        is_verified: true,
      });

      toast.success('Success', {
        description: 'Translation verified',
      });

      if (selectedKey) {
        await fetchTranslationsForKey(selectedKey.id);
      }
    } catch (error) {
      console.error('Error verifying translation:', error);
      toast.error('Error', {
        description: 'Failed to verify translation',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {/* TRUS-446 — tenant content had no entry point from this screen at
            all: registration was an API call and the outstanding report was
            unreachable, so an org could not see, let alone translate, its own
            field and form labels. */}
        <Button
          variant="outline"
          onClick={handleRegisterTenantContent}
          disabled={registering}
        >
          {registering ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t('admin.registerTenantContent', 'Scan fields & forms')}
        </Button>
        <Button variant="outline" onClick={fetchOutstanding} disabled={loadingOutstanding}>
          {loadingOutstanding ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ClipboardList className="mr-2 h-4 w-4" />
          )}
          {t('admin.outstandingWork', 'Outstanding Spanish')}
        </Button>
        <Dialog open={showAddKeyDialog} onOpenChange={setShowAddKeyDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('admin.add_translation_key', 'Add Translation Key')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('admin.add_new_translation_key', 'Add New Translation Key')}</DialogTitle>
              <DialogDescription>
                {t('admin.create_a_new_translation_key', 'Create a new translation key that can be translated into multiple languages')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="key_name">{t('admin.key_name', 'Key Name')}</Label>
                <Input
                  id="key_name"
                  placeholder="e.g., dashboard.welcome"
                  value={newKey.key_name}
                  onChange={(e) => setNewKey({ ...newKey, key_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="category">{t('admin.category', 'Category')}</Label>
                <Input
                  id="category"
                  placeholder="e.g., navigation, buttons, messages"
                  value={newKey.category}
                  onChange={(e) => setNewKey({ ...newKey, category: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="description">{t('admin.description', 'Description')}</Label>
                <Textarea
                  id="description"
                  placeholder={t('admin.description_for_translators', 'Description for translators')}
                  value={newKey.description}
                  onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="source_text">Source Text (English)</Label>
                <Textarea
                  id="source_text"
                  placeholder={t('admin.english_text', 'English text')}
                  value={newKey.source_text}
                  onChange={(e) => setNewKey({ ...newKey, source_text: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddKeyDialog(false)}>
                {t('admin.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleAddKey}>{t('admin.add_key', 'Add Key')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Separator />

      {/* Language Progress */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.language_progress', 'Language Progress')}</CardTitle>
          <CardDescription>{t('admin.translation_completeness_for_each_language', 'Translation completeness for each language')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {languages.map((lang) => (
              <div key={lang.language_code} className="flex items-center gap-4">
                <Languages className="h-4 w-4 text-muted-foreground" />
                <div className="flex-1">
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {lang.native_name} ({lang.language_name})
                    </span>
                    <span className="text-muted-foreground">
                      {lang.translation_progress}%
                    </span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${lang.translation_progress}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {outstanding && (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('admin.outstandingTitle', 'Outstanding Spanish for tenant content')}
            </CardTitle>
            <CardDescription>
              {t(
                'admin.outstandingSubtitle',
                'Three different kinds of work, which a single "missing" count would hide: never translated, translated but the English has since changed, and translated but never signed off.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-6 text-sm">
              <div>
                <div className="text-2xl font-semibold tabular-nums">{outstanding.missing}</div>
                <div className="text-muted-foreground">{t('admin.outstandingMissing', 'Never translated')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold tabular-nums">{outstanding.stale}</div>
                <div className="text-muted-foreground">{t('admin.outstandingStale', 'English changed since')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold tabular-nums">{outstanding.unverified}</div>
                <div className="text-muted-foreground">{t('admin.outstandingUnverified', 'Awaiting sign-off')}</div>
              </div>
              <div>
                <div className="text-2xl font-semibold tabular-nums">{outstanding.total_keys}</div>
                <div className="text-muted-foreground">{t('admin.outstandingTotal', 'Tenant keys total')}</div>
              </div>
            </div>
            <div className="max-h-[320px] overflow-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.english', 'English')}</TableHead>
                    <TableHead>{t('admin.where', 'Where')}</TableHead>
                    <TableHead>{t('admin.reason', 'Reason')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outstanding.items.map((item) => (
                    <TableRow key={`${item.key_name}:${item.reason}`}>
                      <TableCell className="text-sm">{item.source_text}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {item.context || item.source_kind || '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.reason}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Translation Keys */}
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.translation_keys', 'Translation Keys')}</CardTitle>
            <CardDescription>
              {t('admin.select_a_key_to_manage', 'Select a key to manage its translations')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {(
                [
                  ['all', t('admin.scopeAll', 'All')],
                  ['tenant', t('admin.scopeTenant', 'Your fields & forms')],
                  ['chrome', t('admin.scopeChrome', 'App text')],
                ] as [KeyScope, string][]
              ).map(([value, label]) => (
                <Button
                  key={value}
                  size="sm"
                  variant={scope === value ? 'default' : 'outline'}
                  onClick={() => {
                    setScope(value);
                    setPage(0);
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setPage(0);
                setAppliedSearch(search);
              }}
            >
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('admin.searchKeysOrText', 'Search key name or English text…')}
              />
              <Button type="submit" size="sm" variant="outline">
                <Search className="h-4 w-4" />
              </Button>
            </form>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('admin.key', 'Key')}</TableHead>
                    <TableHead>{t('admin.category_1', 'Category')}</TableHead>
                    <TableHead>{t('admin.status', 'Status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {translationKeys.map((key) => (
                    <TableRow
                      key={key.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelectKey(key)}
                    >
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-2">
                          {key.is_dynamic && (
                            <Badge variant="secondary" className="shrink-0">
                              {t('admin.yours', 'Yours')}
                            </Badge>
                          )}
                          <span className="truncate">{key.key_name}</span>
                        </div>
                        {key.is_dynamic && key.source_text && (
                          <div className="mt-1 font-sans text-xs text-muted-foreground">
                            {key.source_text}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{key.category || 'None'}</Badge>
                      </TableCell>
                      <TableCell>
                        {key.is_active ? (
                          <Badge variant="default">{t('admin.active', 'Active')}</Badge>
                        ) : (
                          <Badge variant="secondary">{t('admin.inactive', 'Inactive')}</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between pt-1 text-sm text-muted-foreground">
              <span className="tabular-nums">
                {t('admin.showingRange', 'Showing {{from}}–{{to}}', {
                  from: translationKeys.length === 0 ? 0 : page * PAGE_SIZE + 1,
                  to: page * PAGE_SIZE + translationKeys.length,
                })}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  // A full page means there is probably another one. The list
                  // endpoint returns no total, so this is the honest signal
                  // rather than a page count that would be a guess.
                  disabled={translationKeys.length < PAGE_SIZE}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Translations for Selected Key */}
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedKey ? `Translations for ${selectedKey.key_name}` : 'Translations'}
            </CardTitle>
            <CardDescription>
              {selectedKey
                ? selectedKey.source_text
                : 'Select a translation key to view its translations'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {selectedKey ? (
              <div className="space-y-4">
                <Button
                  size="sm"
                  onClick={() => {
                    setSelectedTranslation(null);
                    setEditTranslation({ language_code: '', translated_text: '' });
                    setShowEditTranslationDialog(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('admin.add_translation', 'Add Translation')}
                </Button>

                <div className="space-y-3">
                  {languages.map((lang) => {
                    const translation = translations.find(
                      (tr) => tr.language_code === lang.language_code
                    );

                    return (
                      <div
                        key={lang.language_code}
                        className="rounded-lg border p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{lang.native_name}</span>
                            {translation?.is_verified && (
                              <Badge variant="default" className="gap-1">
                                <Check className="h-3 w-3" />
                                {t('admin.verified', 'Verified')}
                              </Badge>
                            )}
                          </div>
                          {translation && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditTranslation(translation)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {!translation.is_verified && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleVerifyTranslation(translation.id)}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                        {translation ? (
                          <p className="text-sm text-muted-foreground">
                            {translation.translated_text}
                          </p>
                        ) : (
                          <p className="text-sm italic text-muted-foreground">
                            {t('admin.no_translation_available', 'No translation available')}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('admin.select_a_translation_key_from', 'Select a translation key from the left to view and manage its translations')}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Translation Dialog */}
      <Dialog open={showEditTranslationDialog} onOpenChange={setShowEditTranslationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedTranslation ? 'Edit Translation' : 'Add Translation'}
            </DialogTitle>
            <DialogDescription>
              {selectedKey?.source_text}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="language">{t('admin.language', 'Language')}</Label>
              <Select
                value={editTranslation.language_code}
                onValueChange={(value) =>
                  setEditTranslation({ ...editTranslation, language_code: value })
                }
                disabled={!!selectedTranslation}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('admin.select_language', 'Select language')} />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((lang) => (
                    <SelectItem key={lang.language_code} value={lang.language_code}>
                      {lang.native_name} ({lang.language_name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="translated_text">{t('admin.translated_text', 'Translated Text')}</Label>
              <Textarea
                id="translated_text"
                value={editTranslation.translated_text}
                onChange={(e) =>
                  setEditTranslation({ ...editTranslation, translated_text: e.target.value })
                }
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditTranslationDialog(false)}
            >
              {t('admin.cancel_1', 'Cancel')}
            </Button>
            <Button onClick={handleAddOrUpdateTranslation}>{t('admin.save', 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
